import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  Stack,
  Typography,
  UpdateIconButton,
  ValidationErrors,
  formatBoolean,
  formatPhoneNumber,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { Input, Select } from "components";
import { Permission, PermissionAction, Staff, StaffEditResource, StaffViewResource } from "generated/openApi";

import produce from "immer";
import { isAuthorized } from "services";

export const StaffBasicInfo = (props: {
  staff: Staff;
  setStaff: (value: Staff) => void;
  viewResource: StaffViewResource;
  editResource: StaffEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
  reroute?: (id: number, crudMode: CrudMode) => void;
  hasEditButton?: boolean;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const id = props.staff.id;
  const properties = props.viewResource.properties || [];

  const lastNameField = () => {
    const propertyName = pn<Staff>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.staff.lastName}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<Staff>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.staff.firstName}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const identificationField = () => {
    const propertyName = pn<Staff>("identification");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.identification}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.staff.identification}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.identification = e.target.value;
            })
          )
        }
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<Staff>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.email}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.staff.email}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const cellPhoneField = () => {
    const propertyName = pn<Staff>("cellPhone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label="Phone">{formatPhoneNumber(props.staff.cellPhone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.staff.cellPhone}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.cellPhone = e.target.value;
            })
          )
        }
      />
    );
  };

  const positionField = () => {
    const propertyName = pn<Staff>("staffPosition");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.staffPosition?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.positions?.find((x) => x.id === props.staff.staffPositionId)}
        options={props.editResource.positions}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.staffPositionId = value?.id;
            })
          )
        }
      />
    );
  };

  const disciplineField = () => {
    const propertyName = pn<Staff>("disciplineId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.discipline?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.disciplines?.find((x) => x.id === props.staff.disciplineId)}
        options={props.editResource.disciplines}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.disciplineId = value?.id;
            })
          )
        }
      />
    );
  };

  const employmentStatusField = () => {
    const propertyName = pn<Staff>("employmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.staff.employmentStatus?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.employmentStatuses?.find((x) => x.id === props.staff.employmentStatusId)}
        options={props.editResource.employmentStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.employmentStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const backgroundCheckField = () => {
    const propertyName = pn<Staff>("backgroundCheck");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.staff.backgroundCheck)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.staff.backgroundCheck)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.backgroundCheck = value?.id;
            })
          )
        }
      />
    );
  };

  const cprFirstAidCertifiedField = () => {
    const propertyName = pn<Staff>("cprFirstAidCertified");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.staff.cprFirstAidCertified)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.staff.cprFirstAidCertified)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.cprFirstAidCertified = value?.id;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<Staff>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.staff.note}
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        multiline
        value={props.staff.note}
        onChange={(e) =>
          props.setStaff(
            produce(props.staff, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Card
      header={
        <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
          <Typography variant="inherit">Info</Typography>

          {props.hasEditButton &&
            props.crudMode === "view" &&
            isAuthorized(Permission.Staff, PermissionAction.Update) && (
              <UpdateIconButton onClick={() => props.reroute?.(id, "edit")} />
            )}
        </Stack>
      }
    >
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {identificationField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {firstNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {lastNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {emailField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {cellPhoneField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {positionField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {disciplineField()}
        </Grid>

        {/* <Grid item xs={smallFieldBreakpoints}>
          {employmentStatusField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {backgroundCheckField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {cprFirstAidCertifiedField()}
        </Grid> */}

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>
      </Grid>
    </Card>
  );
};
